// "Hay una versión nueva" for a tab left open for days (docs/PARITY.md P004). The natives poll a
// manifest; the web's "manifest" is the deploy's `version.txt` (deploy-web.sh writes the short commit
// it built; the build emits the same id — vite.config.ts `satoriVersionFile`). A navigation already
// picks up a deploy (sw.js is network-first for pages); this is for the tab that never navigates.
//
// Cadence = the natives' (Windows `UpdateCheckThrottle`, Mac/Android `UpdateCheckThrottle`): a check
// at most every 10 min (coming back to the tab asks again past that floor) and a foreground loop every
// 15 min. The banner decision re-proves "different from what is running" and honours «Ahora no» for
// that one version (`UpdateBannerDecision`).

export const UpdateCheckThrottle = {
  checkIntervalMs: 10 * 60_000,
  recheckIntervalMs: 15 * 60_000,

  /** Gate: true when no check happened in the last 10 minutes (the caller then records `now`). */
  shouldCheck(lastCheckedAt: number | null, now: number): boolean {
    return lastCheckedAt === null || now - lastCheckedAt >= UpdateCheckThrottle.checkIntervalMs;
  },
};

/** A served `version.txt` body reduced to the build id it names, or null (empty, HTML fallback, junk). */
export function servedBuildId(body: string | null | undefined): string | null {
  const s = (body ?? '').trim();
  // A commit id (deploy-web.sh) or the build's own id: short hex, or `dev-<time>` from an untracked tree.
  return /^(?:[0-9a-f]{6,40}|dev-[0-9a-z]+)$/i.test(s) ? s.toLowerCase() : null;
}

/** Same build: equal, or one abbreviation of the other (two `--short` runs may pick different lengths). */
export function sameBuild(running: string, served: string): boolean {
  const a = running.trim().toLowerCase();
  const b = served.trim().toLowerCase();
  if (!a || !b) return true;
  return a === b || a.startsWith(b) || b.startsWith(a);
}

export const UpdateBannerDecision = {
  /** The build to offer, or null: a served build that differs from the running one and was not
   *  waved away with «Ahora no». An unreadable answer never offers anything. */
  offered(running: string, servedBody: string | null | undefined, dismissed: string | null): string | null {
    const served = servedBuildId(servedBody);
    if (!served || sameBuild(running, served)) return null;
    if (dismissed && sameBuild(dismissed, served)) return null;
    return served;
  },
};
